import { Grid, Stack } from '@mui/material';
import { MprcClickable, MprcIconButtonMoreOptionsDropdown } from "components";
import { AutoCompleteDataModel } from 'components/MprcAutoComplete';
import { MprcCardTypography } from 'components/MprcCardTypography';
import { MprcDropdownBuildGroupedItems, MprcDropdownItem } from 'components/MprcIconButtonDropdown';
import { CardAction, CardActionLocation, CardActionType, CreateEnrollResults, EnrollStatus, EvolutionPermissions, PublicationType, ShareTrainingTarget, SystemFeature, TrainingUserInteractionOption } from "generated/CoreApi_v1";
import HtmlUtils from 'lib/HtmlUtils';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { NavigateFunction, useLocation, useNavigate } from 'react-router';
import { DashboardPaths } from 'routes/DashboardRouter';
import { KnowledgePaths } from 'routes/KnowledgeRouter';
import { LmsPaths } from 'routes/LmsRouter';
import { RootPaths } from 'routes/RootRouter';
import { AssDeployDeclineDialog } from 'views/assessment/assDeploy/dialogs/AssDeployDeclineDialog';
import { AssPlayerEvaluation } from 'views/assessment/player/AssPlayerEvaluation';
import { AssPlayerSurvey } from 'views/assessment/player/AssPlayerSurvey';
import { UserPanelItem } from 'views/dashboard/UserPanel';
import { NewDirectMessageDialog } from 'views/foundation/directMessage/NewDirectMessageDialog';
import { discoverPath, ICardDtoActionsProps, ICardDtoPathResult, RenderCardIcon } from 'views/general/Cards/Common';
import { AddToListDrawer } from 'views/knowledge/components/card/actions/AddToListDrawer';
import { publicationFactory } from 'views/knowledge/feed/FeedHelpers';
import { useAddPublicationToSeeLaterList } from 'views/knowledge/hooks/useAddPublicationToSeeLaterList';
import { PublicationViewerDialog } from 'views/knowledge/publication/dialogs/PublicationViewerDialog';
import { DoTrainingBoomark, TrainingBookmarkCardButton } from 'views/lms/components/TrainingBookmarkButton';
import { TrainingLikeCardButton } from 'views/lms/components/TrainingLikeButton';
import { TrainingShareDialog } from 'views/lms/dialogs/TrainingShareDialog';
import { MeetingViewer } from 'views/lms/meeting/views/MeetingViewer';
import { PostExclusiveViewer } from 'views/lms/publication/views/PostExclusiveViewer';
import { WaitingApprovalDrawer } from 'views/lms/student/EnrollmentView/drawers/WaitingApprovalDrawer';
import { ConclusionFormDialog } from 'views/lms/student/EnrollmentView/ECourse/ConclusionFormDialog';
import { TrainingRegistrationDialog } from 'views/lms/student/Registration/TrainingRegistrationDialog';
import { TrainingEditRating } from 'views/lms/student/TrainingRatingView/TrainingEditRating';
///////////////////////////////////////////////////////////////////////////////

export const resolveLinkFeature = (feature: SystemFeature, setDialog: any, navigate: NavigateFunction) => {
    switch (feature) {
        case SystemFeature.Challenge:
            navigate(LmsPaths.TopicsChallenge);
            break;
        case SystemFeature.DevelopmentPlan:
            break;
        case SystemFeature.KnowledgeCreatePublication:
            setDialog(publicationFactory(null, PublicationType.Post, () => setDialog(null)));
            break;
        case SystemFeature.KnowledgeFavorites:
            navigate(KnowledgePaths.FeedFavorites);
            break;
        case SystemFeature.KnowledgeSeeLater:
            navigate(KnowledgePaths.FeedSeeLater);
            break;
        case SystemFeature.KnowledgeHome:
            navigate(KnowledgePaths.FeedStartup);
            break;
        case SystemFeature.KnowledgeInterests:
            navigate(RootPaths.UserProfileInterest);
            break;
        case SystemFeature.KnowledgeQA:
            navigate(KnowledgePaths.FeedQuestionAndAnswers);
            break;
        case SystemFeature.KnowledgeShorts:
            navigate(KnowledgePaths.FeedShorts);
            break;
        case SystemFeature.MeetingCalendar:
            navigate(LmsPaths.StudentMeetings);
            break;
        case SystemFeature.TrainingCatalog:
            navigate(LmsPaths.StudentCatalog);
            break;
        case SystemFeature.TrainingHistory:
            navigate(LmsPaths.StudentHistory);
            break;
        case SystemFeature.TrainingLibrary:
            navigate(LmsPaths.StudentLibrary);
            break;
        case SystemFeature.TrainingNome:
            navigate(LmsPaths.Home);
            break;
        case SystemFeature.UserAwards:
            navigate(DashboardPaths.UserEvolution, { state: { tab: EvolutionPermissions.Award } });
            break;
        case SystemFeature.UserProfileView:
            navigate(RootPaths.UserProfile);
            break;
        case SystemFeature.UserProfileEdit:
            navigate(RootPaths.EditSelfProfile);
            break;
        case SystemFeature.UserProgress:
            navigate(DashboardPaths.UserEvolution, { state: { tab: EvolutionPermissions.Training } });
            break;
    }
};

///////////////////////////////////////////////////////////////////////////////

/**
 * Resolve a LMS target action
 * @param action Action to execute
 * @param data Data to use in the action
 * @param setDialog Function to set a dialog
 * @param handleCloseRegistration Function to close a dialog
 * @param handleCloseWithPartialReload Function to close a dialog and reload the carousel
 * @param handleCloseAssPlayer Function to close a dialog and reload the carousel
 * @param navigate Function to navigate
 * @returns True if the action was executed
 */
export const resolveLmsTargetAction = (
    action: CardAction,
    data: Record<string, any>,
    setDialog: (data: any) => void,
    handleCloseRegistration: (result: CreateEnrollResults | undefined) => void,
    handleCloseWithPartialReload: (reload?: boolean) => void,
    handleCloseAssPlayer: (success: boolean) => void,
    navigate: (pathresult: ICardDtoPathResult) => void,
) => {
    switch (action.actionType) {
        case CardActionType.LmsLibraryView:
        case CardActionType.LmsRegistrationView:
        case CardActionType.LmsEnrollmentView:
            const pathResult = discoverPath(action, data);
            if (pathResult) navigate(pathResult);
            return true;
        case CardActionType.LmsRegistrationOneClick:
            setDialog(<TrainingRegistrationDialog trainingId={data["trainingId"]!} masterEnrollmentId={data["masterEnrollmentId"]} immediateRegistration={true} onClose={handleCloseRegistration} />);
            return true;
        case CardActionType.LmsRegistration:
            setDialog(<TrainingRegistrationDialog trainingId={data["trainingId"]!} masterEnrollmentId={data["masterEnrollmentId"]} classroomId={data["targetClassroomId"]} onClose={handleCloseRegistration} />);
            return true;
        case CardActionType.LmsEditConclusionForm:
            setDialog(<ConclusionFormDialog trainingId={data["trainingId"]} classroomId={data["classroomId"]!} enrollmentId={data["enrollmentId"]!} onClose={handleCloseRegistration} />);
            return true;
        case CardActionType.LmsFillConclusionForm:
            setDialog(<ConclusionFormDialog trainingId={data["trainingId"]} classroomId={data["classroomId"]!} enrollmentId={null} onClose={handleCloseRegistration} />);
            return true;
        case CardActionType.LmsTrainingInfo:
            setDialog(<TrainingRegistrationDialog trainingId={data["trainingId"]!} masterEnrollmentId={data["masterEnrollmentId"]} readonly={true} onClose={handleCloseRegistration} />);
            return true;
        case CardActionType.LmsAddToLibrary:
            setDialog(
                <DoTrainingBoomark
                    trainingId={data["trainingId"]!}
                    option={TrainingUserInteractionOption.Bookmark}
                    onClose={handleCloseWithPartialReload}
                />
            );
            return true;
        case CardActionType.LmsRateAndWriteComment:
            setDialog(
                <TrainingEditRating
                    enrollmentId={data["enrollmentId"]!}
                    trainingName={data["trainingName"]}
                    onClose={handleCloseWithPartialReload}
                />
            );
            return true;
        case CardActionType.LmsShareInChannel:
            setDialog(
                <TrainingShareDialog
                    shareOption={ShareTrainingTarget.Channel}
                    onClose={handleCloseWithPartialReload}
                    trainingId={data["trainingId"]}
                    trainingName={data["trainingName"]}
                />
            );
            return true;
        case CardActionType.LmsShareInList:
            setDialog(
                <TrainingShareDialog
                    shareOption={ShareTrainingTarget.List}
                    onClose={handleCloseWithPartialReload}
                    trainingId={data["trainingId"]}
                    trainingName={data["trainingName"]}
                />
            );
            break;
        case CardActionType.LmsShowWorkflowStatus:
            setDialog(
                <WaitingApprovalDrawer
                    enrollmentId={data["enrollmentId"]}
                    trainingName={data["trainingName"]}
                    onClose={() => handleCloseWithPartialReload(false)}
                />
            );
            return true;
        case CardActionType.OpenSurvey:
            setDialog(
                <AssPlayerSurvey
                    assessmentId={data["assessmentId"]}
                    deployId={data["deployId"]}
                    deployParticipantId={data["deployParticipantId"]}
                    mode="play"
                    onClose={(success) => handleCloseAssPlayer(success)}
                />
            );
            return true;
        case CardActionType.OpenEvaluation:
            setDialog(
                <AssPlayerEvaluation
                    deployParticipantId={data["deployParticipantId"]}
                    deployId={data["deployId"]}
                    assessmentId={data["assessmentId"]}
                    instanceId={data["instanceId"]}
                    onClose={(success) => handleCloseAssPlayer(success)}
                />
            );
            return true;
        case CardActionType.OpenManagerEfficacy:
            navigate({ url: LmsPaths.ManagerEfficacy });
            return true;
        case CardActionType.DeclineSurvey:
            setDialog(
                <AssDeployDeclineDialog
                    deployId={data["deployId"]}
                    title={data["title"] ?? "?"}
                    onClose={() => handleCloseWithPartialReload(true)}
                />
            );
            return true;
        case CardActionType.OpenMeetingLink:
            if (data["meetingUrl"]) {
                HtmlUtils.openUrlInNewWindow(data["meetingUrl"]);
            }
            return true;

        case CardActionType.MeetingView:
            setDialog(
                <MeetingViewer
                    componentId={data["meetingComponentId"]}
                    classroomId={data["classroomId"]}
                    meetingType={data["meetingType"]}
                    open
                    onClose={() => setDialog(null)}
                />
            );
            return true;

    }

    return false;
};

///////////////////////////////////////////////////////////////////////////////

export const CardDtoActions = (props: ICardDtoActionsProps) => {

    const { item } = props;

    const primaryAction = item.actions.find(p => p.location == CardActionLocation.Primary);
    const secondaryAction = item.actions.find(p => p.location == CardActionLocation.Secondary);
    const overflowActions = item.actions.filter(p => p.location == CardActionLocation.Overflow);
    const buttonReaction = item.actions.find(p => p.location == CardActionLocation.Button);

    const [buttonReactionState, setButtonReactionState] = useState<boolean | undefined>(buttonReaction?.actionButtonState ?? undefined);
    const [action, setAction] = useState<CardAction | null>();
    const [, postingSeeLater] = useAddPublicationToSeeLaterList();

    const data = item.data;

    ///
    const handleSelectOverflow = (selectedValue: CardActionType) => {
        const action = overflowActions.find(p => p.actionType == selectedValue);
        if (action) setAction(action);
    };

    // Cria Grupo de Opções no OVERFLOW
    const overflowOptions: MprcDropdownItem<CardActionType>[][] = [];
    const overflowGroups = [...new Set(overflowActions.map(p => p.locationGroup))];
    overflowGroups.forEach((g) => {
        overflowOptions.push(
            overflowActions
                .filter(x => x.locationGroup == g)
                .map(a => ({
                    text: a.text,
                    value: a.actionType,
                    disabled: postingSeeLater && a.actionType == CardActionType.PublicationAddToViewLater
                } as MprcDropdownItem<CardActionType>))
        );
    });

    return (
        <>
            {action && <CardRenderActions {...props} cardAction={action} endAction={() => setAction(null)} />}

            <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%" sx={{ height: 34 }}>
                <Grid container justifyContent="space-between" alignItems="center" width="100%">
                    <Grid>
                        {
                            primaryAction &&
                            <MprcCardTypography variant="link">
                                <RenderCardIcon icon={primaryAction.icon} />
                                <MprcClickable onClick={() => setAction(primaryAction)} disabled={primaryAction.actionType == CardActionType.None}>
                                    {primaryAction.text}
                                </MprcClickable>
                            </MprcCardTypography>
                        }
                    </Grid>
                    {
                        secondaryAction &&
                        <Grid>
                            <MprcCardTypography variant="link">
                                <RenderCardIcon icon={secondaryAction.icon} />
                                <MprcClickable onClick={() => setAction(secondaryAction)} disabled={secondaryAction.actionType == CardActionType.None}>
                                    {secondaryAction.text}
                                </MprcClickable>
                            </MprcCardTypography>
                        </Grid>
                    }
                </Grid>

                {
                    buttonReaction && buttonReaction.actionType == CardActionType.LmsLikedReaction &&
                    <TrainingLikeCardButton
                        readonly={false}
                        trainingId={data["trainingId"]!}
                        liked={buttonReactionState}
                        onChange={(newValue) => setButtonReactionState(newValue)}
                    />
                }

                {
                    buttonReaction && buttonReaction.actionType == CardActionType.LmsBookmarkedReaction &&
                    <TrainingBookmarkCardButton
                        readonly={false}
                        trainingId={data["trainingId"]!}
                        bookmarked={buttonReactionState}
                        onChange={(newValue) => setButtonReactionState(newValue)}
                    />
                }

                {
                    overflowActions.length > 0 &&
                    <MprcIconButtonMoreOptionsDropdown
                        color="highlight"
                        onChange={(selectedValue) => handleSelectOverflow(selectedValue)}
                        items={MprcDropdownBuildGroupedItems(overflowOptions)}
                    />
                }
            </Stack>
        </>
    );
};

interface ICardRenderAction extends ICardDtoActionsProps {
    cardAction?: CardAction | null;
    endAction: () => void;
}

export const CardRenderActions = ({ item, onCarouselReload, onFullReload, onClearCache, saveState, cardAction, endAction }: ICardRenderAction) => {
    const [dialog, setDialog] = useState<any>(null);
    const [addToSeeLater] = useAddPublicationToSeeLaterList();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const location = useLocation();
    const data = item.data;

    const closeDialog = () => {
        endAction();
        setDialog(null);
    };

    ///
    const handleNavigate = (pathResult: ICardDtoPathResult) => {
        endAction();
        if (saveState) {
            navigate(document.location.search, { replace: true, state: { ...(location.state ?? {}), saveState } });
            navigate(pathResult.url, { state: pathResult.state });
        } else {
            navigate(pathResult.url, { state: pathResult.state });
        }
    };

    const handleCloseDialogPartialReload = (reload?: boolean) => {
        closeDialog();
        if (reload && onCarouselReload) onCarouselReload();
    };

    const handleCloseDialogRegistration = (result: CreateEnrollResults | undefined) => {
        closeDialog();
        if (result) {
            if (onClearCache) onClearCache();

            if (result.enrollmentId && result.enrollStatus == EnrollStatus.NotStarted) {
                navigate(LmsPaths.BuildEnrollmentViewer(data.trainingType!, result.enrollmentId));
            }
            else onFullReload && onFullReload();
        }
    };

    const handleCloseAssPlayer = (success: boolean) => {
        closeDialog();
        if (success) {
            if (onClearCache) onClearCache();
            onFullReload && onFullReload();
        } else if (onCarouselReload) {
            onCarouselReload();
        }
    };

    ///
    const handleSelection = (action: CardAction) => {
        switch (action.actionType) {
            case CardActionType.PublicationAddToList:
                setDialog(
                    <AddToListDrawer
                        publicationId={data["publicationId"]!}
                        onChanged={() => onCarouselReload && onCarouselReload()}
                        subtitle={item.data["title"]}
                        onClose={handleCloseDialogPartialReload}
                    />
                );
                break;
            case CardActionType.PublicationAddToViewLater:
                addToSeeLater(data["publicationId"]!);
                break;
            case CardActionType.PublicationPreview:
                if (data["publicationType"]! == PublicationType.TrainingPost) {
                    setDialog(<PostExclusiveViewer
                        open
                        publicationId={data["publicationId"]!}
                        onClose={() => closeDialog()}
                    />);
                } else {
                    setDialog(<PublicationViewerDialog
                        publicationId={data["publicationId"]!}
                        onClose={() => closeDialog()}
                    />);
                }
                break;
            case CardActionType.AskToUser:
                setDialog(publicationFactory(
                    null,
                    PublicationType.Question,
                    () => handleCloseDialogPartialReload(false),
                    { responderId: item.data["userId"], responderName: item.data["name"] }
                ));
                break;
            case CardActionType.DirectMessage:
                setDialog(
                    <NewDirectMessageDialog
                        recipients={[{ key: item.data["userId"], label: item.data["name"], thumbnailUrl: item.thumbnail!.url } as AutoCompleteDataModel]}
                        onClose={() => handleCloseDialogPartialReload(false)}
                    />
                );
                break;
            case CardActionType.UserIndicators:
                navigate(DashboardPaths.UserPanel, { state: { user: { userId: item.data["userId"], name: item.data["name"], thumbnailUrl: item.thumbnail!.url } as UserPanelItem } });
                break;

            case CardActionType.LmsShareInList:
                setDialog(
                    <TrainingShareDialog
                        shareOption={ShareTrainingTarget.List}
                        onClose={() => handleCloseDialogPartialReload(false)}
                        trainingId={data["trainingId"]}
                        trainingName={data["trainingName"]}
                    />
                );
                break;

            default:
                const handled = resolveLmsTargetAction(
                    action
                    , data
                    , setDialog
                    , handleCloseDialogRegistration, handleCloseDialogPartialReload, handleCloseAssPlayer
                    , handleNavigate);

                if (!handled) {
                    const pathResult = discoverPath(action, data);
                    if (pathResult) {
                        handleNavigate(pathResult);
                    }
                    else {
                        endAction();
                        enqueueSnackbar("Ação não implementada", { variant: "error" });
                    }
                }
                break;
        }
    };


    ///
    useEffect(() => {
        if (cardAction)
            handleSelection(cardAction);
    }, [cardAction]);

    return (
        <>
            {dialog}
        </>
    );
};
